/** Attribute a fixed portfolio without changing funder budgets or blocked edges. */
export type EligibleFundingInput = {
  supplies: readonly number[];
  demands: readonly number[];
  eligible: readonly (readonly boolean[])[];
  /** A feasible preferred assignment is retained exactly. Defaults to proportional funding. */
  preferred?: readonly (readonly number[])[];
  /** Optional additional limits on eligible edges, such as the total dollars being phase-split. */
  capacities?: readonly (readonly number[])[];
  /** Hold is always eligible and is filled only after real grants. */
  holdIndex?: number;
};

export type EligibleFundingResult = {
  allocations: number[][];
  unusedSupply: number[];
  unmetDemand: number[];
  /** Floating-point tolerance used for this flow and its capacity cuts. */
  tolerance: number;
  /**
   * A deficient set of proposals from the residual minimum cut. With unrestricted
   * eligible edges, redundant proposals are removed and capacity is the sum of
   * the remaining proposals' eligible funders' supplies.
   * Individual proposal limits miss shortages shared by several proposals.
   */
  bottleneck?: {
    proposalIndices: number[];
    funderIndices: number[];
    demand: number;
    capacity: number;
  };
};

type Edge = { to: number; remaining: number; reverse: Edge; cost: number };

export const FUNDING_FLOW_TOLERANCE = 1e-7;

/** Scale numerical slack to the dollars being summed. At billion-dollar
 * budgets one ULP exceeds the absolute floor; interpreting it as a deficient
 * cut can hide a different, materially unaffordable grant group. */
export function fundingFlowTolerance(
  supplies: readonly number[],
  demands: readonly number[],
): number {
  const scale = Math.max(
    supplies.reduce((total, amount) => total + amount, 0),
    demands.reduce((total, amount) => total + amount, 0),
  );
  return Math.max(FUNDING_FLOW_TOLERANCE, scale * Number.EPSILON * 16);
}

function createFundingNetwork(nodeCount: number): {
  graph: Edge[][];
  addEdge: (from: number, to: number, capacity: number, cost?: number) => Edge;
} {
  const graph: Edge[][] = Array.from({ length: nodeCount }, () => []);

  const addEdge = (
    from: number,
    to: number,
    capacity: number,
    cost = 0,
  ): Edge => {
    // The pair is linked before either edge can be visited.
    const forward = { to, remaining: capacity, cost } as Edge;
    const reverse = { to: from, remaining: 0, reverse: forward, cost: -cost };
    forward.reverse = reverse;
    graph[from]!.push(forward);
    graph[to]!.push(reverse);
    return forward;
  };

  return { graph, addEdge };
}

function sendFunding(edge: Edge, amount: number): void {
  edge.remaining -= amount;
  edge.reverse.remaining += amount;
}

function augmentFundingPath(
  parents: (Edge | undefined)[],
  source: number,
  sink: number,
): void {
  let amount = Infinity;

  for (let node = sink; node !== source;) {
    const edge = parents[node]!;
    amount = Math.min(amount, edge.remaining);
    node = edge.reverse.to;
  }

  for (let node = sink; node !== source;) {
    const edge = parents[node]!;
    sendFunding(edge, amount);
    node = edge.reverse.to;
  }
}

export function allocateEligibleFunding({
  supplies,
  demands,
  eligible,
  preferred,
  capacities,
  holdIndex,
}: EligibleFundingInput): EligibleFundingResult {
  const tolerance = fundingFlowTolerance(supplies, demands);
  const numFunders = supplies.length;
  const numProposals = demands.length;
  const source = numFunders + numProposals;
  const sink = source + 1;
  const { graph, addEdge } = createFundingNetwork(sink + 1);
  const sourceEdges = supplies.map((supply, row) =>
    addEdge(source, row, supply),
  );
  const proposalEdges = demands.map((demand, col) =>
    addEdge(numFunders + col, sink, col === holdIndex ? 0 : demand),
  );
  const funderEdges = supplies.map((_, row) =>
    demands.map((_, col) =>
      addEdge(
        row,
        numFunders + col,
        col === holdIndex
          ? (capacities?.[row]?.[col] ?? Infinity)
          : eligible[row]?.[col]
            ? (capacities?.[row]?.[col] ?? Infinity)
            : 0,
      ),
    ),
  );
  const totalSupply = supplies.reduce((total, amount) => total + amount, 0);

  const fillPreferredColumn = (col: number): void => {
    for (let row = 0; row < numFunders; row++) {
      const amount = Math.min(
        preferred?.[row]?.[col] ??
          (totalSupply > 0
            ? (supplies[row]! / totalSupply) * demands[col]!
            : 0),
        sourceEdges[row]!.remaining,
        funderEdges[row]![col]!.remaining,
        proposalEdges[col]!.remaining,
      );

      if (amount > 0) {
        sendFunding(sourceEdges[row]!, amount);
        sendFunding(funderEdges[row]![col]!, amount);
        sendFunding(proposalEdges[col]!, amount);
      }
    }
  };

  // Existing proportional assignments can seed the flow, but hold must not
  // consume the only eligible money for a real grant in an infeasible portfolio.
  for (let col = 0; col < numProposals; col++) {
    if (col !== holdIndex) {
      fillPreferredColumn(col);
    }
  }

  const augment = (): boolean[] => {
    while (true) {
      const parents: (Edge | undefined)[] = Array.from({
        length: graph.length,
      });
      const reached = Array<boolean>(graph.length).fill(false);
      const queue = [source];
      reached[source] = true;

      for (let next = 0; next < queue.length && !reached[sink]; next++) {
        for (const edge of graph[queue[next]!]!) {
          // Several small residual paths can fund a material combined amount.
          // Apply accounting tolerance to the final result, not individual edges.
          if (edge.remaining <= 0 || reached[edge.to]) {
            continue;
          }

          reached[edge.to] = true;
          parents[edge.to] = edge;
          queue.push(edge.to);
        }
      }

      if (!reached[sink]) {
        return reached;
      }

      augmentFundingPath(parents, source, sink);
    }
  };

  let reached = augment();

  if (holdIndex != null && proposalEdges[holdIndex] != null) {
    proposalEdges[holdIndex].remaining = demands[holdIndex]!;
    fillPreferredColumn(holdIndex);
    reached = augment();
  }

  const allocations = funderEdges.map((row) =>
    row.map((edge) => edge.reverse.remaining),
  );
  const unusedSupply = sourceEdges.map((edge) => Math.max(0, edge.remaining));
  const unmetDemand = proposalEdges.map((edge) => Math.max(0, edge.remaining));
  const result: EligibleFundingResult = {
    allocations,
    unusedSupply,
    unmetDemand,
    tolerance,
  };

  if (unmetDemand.some((amount) => amount > tolerance)) {
    let proposalIndices = demands.flatMap((demand, col) =>
      demand > 0 && !reached[numFunders + col] ? [col] : [],
    );
    let funderIndices = supplies.flatMap((supply, row) =>
      supply > 0 && !reached[row] ? [row] : [],
    );
    let capacity = funderIndices.reduce(
      (total, row) => total + supplies[row]!,
      0,
    );

    // Finite edge caps can also cross the cut; unrestricted eligibility edges
    // never do, since they would have made the proposal reachable.
    for (let row = 0; row < numFunders; row++) {
      if (!reached[row]) {
        continue;
      }

      for (const col of proposalIndices) {
        const edge = funderEdges[row]![col]!;
        capacity += edge.remaining + edge.reverse.remaining;
      }
    }

    if (capacities == null) {
      const neighbors = (columns: number[]): number[] =>
        supplies.flatMap((supply, row) =>
          supply > 0 &&
          columns.some((col) => col === holdIndex || eligible[row]?.[col])
            ? [row]
            : [],
        );
      const demandOf = (columns: number[]): number =>
        columns.reduce((total, col) => total + demands[col]!, 0);
      const capacityOf = (columns: number[]): number =>
        neighbors(columns).reduce((total, row) => total + supplies[row]!, 0);
      // A residual cut can include a fully paid, independent grant. Capping
      // that grant would strand eligible money without relieving the shortage.
      // Grants with the same eligible funders stay together, so the shortage is
      // shared in their original proportions. Trimming them individually would
      // make the result depend on proposal order and require a rerun per grant.
      // Remove already paid groups first while the remaining set is deficient.
      let removed: boolean;

      do {
        removed = false;
        const groups = new Map<string, number[]>();

        for (const col of proposalIndices) {
          const key = neighbors([col]).join(",");
          const group = groups.get(key) ?? [];
          group.push(col);
          groups.set(key, group);
        }

        const candidates = [...groups.values()].sort(
          (a, b) =>
            Number(a.some((col) => unmetDemand[col]! > tolerance)) -
            Number(b.some((col) => unmetDemand[col]! > tolerance)),
        );

        for (const group of candidates) {
          const remaining = proposalIndices.filter(
            (candidate) => !group.includes(candidate),
          );

          if (demandOf(remaining) - capacityOf(remaining) > tolerance) {
            proposalIndices = remaining;
            removed = true;
          }
        }
      } while (removed);

      funderIndices = neighbors(proposalIndices);
      capacity = capacityOf(proposalIndices);
    }

    result.bottleneck = {
      proposalIndices,
      funderIndices,
      demand: proposalIndices.reduce((total, col) => total + demands[col]!, 0),
      capacity,
    };
  }

  return result;
}

export type EligibleMatchingFundingInput = Pick<
  EligibleFundingInput,
  "supplies" | "demands" | "eligible" | "holdIndex"
> & {
  matchingSupplies: readonly number[];
  matchingDemands: readonly number[];
};

/**
 * Assign all grants and their matching pledges together. Matching labels have
 * cost -1, so a minimum-cost complete flow pledges the greatest feasible amount.
 * Residual paths can move earlier payments between funders; fixing total payments
 * before assigning matching can otherwise discard a feasible pledge.
 *
 * Successive shortest augmenting paths start from zero flow. Each augmentation
 * preserves minimum cost for the total flow reached so far; Bellman-Ford handles
 * the negative matching edges and the reverse edges used to undo prior choices.
 */
export function allocateEligibleFundingWithMatching({
  supplies,
  demands,
  eligible,
  matchingSupplies,
  matchingDemands,
  holdIndex,
}: EligibleMatchingFundingInput): Omit<EligibleFundingResult, "bottleneck"> & {
  matchingAllocations: number[][];
} {
  const tolerance = fundingFlowTolerance(supplies, demands);
  const numFunders = supplies.length;
  const numProposals = demands.length;
  const matchingFunderOffset = numFunders;
  const proposalOffset = 2 * numFunders;
  const matchingProposalOffset = proposalOffset + numProposals;
  const source = matchingProposalOffset + numProposals;
  const sink = source + 1;
  const { graph, addEdge } = createFundingNetwork(sink + 1);
  const sourceEdges = supplies.map((supply, row) =>
    addEdge(source, row, supply),
  );

  for (let row = 0; row < numFunders; row++) {
    addEdge(row, matchingFunderOffset + row, matchingSupplies[row] ?? 0, -1);
  }

  const proposalEdges = demands.map((demand, col) =>
    addEdge(proposalOffset + col, sink, demand),
  );

  for (let col = 0; col < numProposals; col++) {
    addEdge(
      matchingProposalOffset + col,
      proposalOffset + col,
      matchingDemands[col] ?? 0,
    );
  }

  const standardEdges = supplies.map((_, row) =>
    demands.map((_, col) =>
      addEdge(
        row,
        proposalOffset + col,
        col === holdIndex || eligible[row]?.[col] ? Infinity : 0,
      ),
    ),
  );
  const matchingEdges = supplies.map((_, row) =>
    demands.map((_, col) =>
      addEdge(
        matchingFunderOffset + row,
        matchingProposalOffset + col,
        col === holdIndex || eligible[row]?.[col] ? Infinity : 0,
      ),
    ),
  );

  while (true) {
    const distances = Array<number>(graph.length).fill(Infinity);
    const parents: (Edge | undefined)[] = Array.from({ length: graph.length });
    distances[source] = 0;

    for (let iteration = 0; iteration < graph.length - 1; iteration++) {
      let changed = false;

      for (let from = 0; from < graph.length; from++) {
        for (const edge of graph[from]!) {
          if (edge.remaining <= 0) {
            continue;
          }

          const distance = distances[from]! + edge.cost;

          if (distance < distances[edge.to]!) {
            distances[edge.to] = distance;
            parents[edge.to] = edge;
            changed = true;
          }
        }
      }

      if (!changed) {
        break;
      }
    }

    if (parents[sink] == null) {
      break;
    }

    augmentFundingPath(parents, source, sink);
  }

  const matchingAllocations = matchingEdges.map((row) =>
    row.map((edge) => edge.reverse.remaining),
  );
  return {
    allocations: standardEdges.map((row, f) =>
      row.map(
        (edge, p) => edge.reverse.remaining + matchingAllocations[f]![p]!,
      ),
    ),
    matchingAllocations,
    tolerance,
    unusedSupply: sourceEdges.map((edge) => Math.max(0, edge.remaining)),
    unmetDemand: proposalEdges.map((edge) => Math.max(0, edge.remaining)),
  };
}

/**
 * Convert a limit on one recommender's payment into its endorsement limit.
 * Proposal caps constrain guarantees, while funder eligibility constrains cash.
 * Invert the same shared-level cost bands used by accountsOfGuarantees, holding
 * the other recommenders' guarantees fixed for the next allocation rerun.
 */
export function capGuaranteeForAccount({
  targetAccount,
  evaluatorIndex,
  guarantees,
  responsibilityBudgets,
}: {
  targetAccount: number;
  evaluatorIndex: number;
  guarantees: readonly number[];
  responsibilityBudgets: readonly number[];
}): number {
  const currentGuarantee = guarantees[evaluatorIndex]!;
  const totalBudget = responsibilityBudgets.reduce(
    (total, budget) => total + budget,
    0,
  );
  const weights =
    totalBudget > 0 ? responsibilityBudgets : guarantees.map(() => 1);
  const ownWeight = weights[evaluatorIndex]!;

  if (ownWeight === 0 || currentGuarantee === 0) {
    return currentGuarantee;
  }

  const levels = [
    ...new Set(
      guarantees.filter((level) => level > 0 && level < currentGuarantee),
    ),
    currentGuarantee,
  ].sort((a, b) => a - b);
  let remaining = Math.max(0, targetAccount);
  let previous = 0;

  for (const level of levels) {
    const totalWeight = guarantees.reduce(
      (total, guarantee, index) =>
        total + (guarantee >= level ? weights[index]! : 0),
      0,
    );
    const share = ownWeight / totalWeight;
    const cost = (level - previous) * share;

    if (remaining < cost) {
      return previous + remaining / share;
    }

    remaining -= cost;
    previous = level;
  }

  return currentGuarantee;
}
